import React from 'react';
import { Alert, Table, Spin, Card, Statistic, Row, Col } from 'antd';
import { useParams } from 'react-router-dom';
import { useGetApiAdminGuidesGuideIdInsights } from '../api/generated';
import type { BrandInsight, ModelInsight, EngineTypeInsight } from '../api/generated';
import { useAuth } from '@/shared/lib/auth-context';

// Style for non-bold table headers
const tableStyles = `
  .insights-table .ant-table-thead > tr > th {
    font-weight: normal !important;
  }
`;

interface InsightsTableProps {
  guideId: string;
}

const InsightsTable: React.FC<InsightsTableProps> = ({ guideId }) => {
  const { token, isAuthenticated } = useAuth();
  const { data, isLoading, error } = useGetApiAdminGuidesGuideIdInsights(guideId, {
    query: {
      enabled: !!guideId && !!token && isAuthenticated,
    },
  });

  if (isLoading) {
    return <Spin size="large" />;
  }

  if (error || !data) {
    return (
      <Alert
        message="Error Loading Insights"
        description="Could not load guide insights data."
        type="error"
        showIcon
      />
    );
  }

  const { summary, brandInsights } = data;

  const formatPercentage = (value?: number) => {
    if (value === undefined || value === null) return '0%';
    return `${value.toFixed(0)}%`;
  };

  // Common columns configuration
  const getColumns = (nameKey: string, nameTitle: string) => [
    {
      title: nameTitle,
      dataIndex: nameKey,
      key: nameKey,
    },
    {
      title: 'Views',
      dataIndex: 'views',
      key: 'views',
    },
    {
      title: 'Solved',
      dataIndex: 'solved',
      key: 'solved',
    },
    {
      title: 'Not Solved',
      dataIndex: 'notSolved',
      key: 'notSolved',
    },
    {
      title: 'Solved %',
      dataIndex: 'solvedPercentage',
      key: 'solvedPercentage',
      render: formatPercentage,
    },
    {
      title: 'Engagement %',
      dataIndex: 'engagementPercentage',
      key: 'engagementPercentage',
      render: formatPercentage,
    },
  ];

  // Add keys for antd tables
  const brandsWithKeys = brandInsights?.map((brand, idx) => ({
    ...brand,
    key: `brand-${idx}`,
  })) || [];

  return (
    <div className="insights-table">
      <style>{tableStyles}</style>
      {/* Summary Statistics */}
      {summary && (
        <Card style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={4}>
              <Statistic title="Total Views" value={summary.totalViews} />
            </Col>
            <Col span={4}>
              <Statistic title="Total Solved" value={summary.totalSolved} />
            </Col>
            <Col span={4}>
              <Statistic title="Total Not Solved" value={summary.totalNotSolved} />
            </Col>
            <Col span={4}>
              <Statistic title="Solved %" value={formatPercentage(summary.solvedPercentage)} />
            </Col>
            <Col span={4}>
              <Statistic title="Not Solved %" value={formatPercentage(summary.notSolvedPercentage)} />
            </Col>
            <Col span={4}>
              <Statistic title="Engagement %" value={formatPercentage(summary.engagementPercentage)} />
            </Col>
          </Row>
        </Card>
      )}

      {/* 4-Level Expandable Table */}
      <Table
        columns={getColumns('brand', 'Brand')}
        dataSource={brandsWithKeys}
        pagination={false}
        expandable={{
          expandedRowRender: (brandRecord: BrandInsight) => {
            const modelsWithKeys = brandRecord.models?.map((model, idx) => ({
              ...model,
              key: `model-${brandRecord.brand}-${idx}`,
            })) || [];

            return (
              <div style={{ paddingLeft: 20, paddingTop: 8, paddingBottom: 8 }}>
                <Table
                  columns={getColumns('model', 'Model')}
                  dataSource={modelsWithKeys}
                  pagination={false}
                  size="small"
                  expandable={{
                    expandedRowRender: (modelRecord: ModelInsight) => {
                      const engineTypesWithKeys = modelRecord.engineTypes?.map((engineType, idx) => ({
                        ...engineType,
                        key: `engine-${modelRecord.model}-${idx}`,
                      })) || [];

                      return (
                        <div style={{ paddingLeft: 20, paddingTop: 8, paddingBottom: 8 }}>
                          <Table
                            columns={getColumns('engineType', 'Engine Type')}
                            dataSource={engineTypesWithKeys}
                            pagination={false}
                            size="small"
                            expandable={{
                              expandedRowRender: (engineRecord: EngineTypeInsight) => {
                                const modelYearsWithKeys = engineRecord.modelYears?.map((modelYear, idx) => ({
                                  ...modelYear,
                                  key: `year-${engineRecord.engineType}-${idx}`,
                                })) || [];

                                return (
                                  <div style={{ paddingLeft: 20, paddingTop: 8, paddingBottom: 8 }}>
                                    <Table
                                      columns={getColumns('modelYear', 'Model Year')}
                                      dataSource={modelYearsWithKeys}
                                      pagination={false}
                                      size="small"
                                    />
                                  </div>
                                );
                              },
                              rowExpandable: (record: EngineTypeInsight) => 
                                !!(record.modelYears && record.modelYears.length > 0),
                            }}
                          />
                        </div>
                      );
                    },
                    rowExpandable: (record: ModelInsight) => 
                      !!(record.engineTypes && record.engineTypes.length > 0),
                  }}
                />
              </div>
            );
          },
          rowExpandable: (record: BrandInsight) => 
            !!(record.models && record.models.length > 0),
        }}
      />
    </div>
  );
};

export const GuideInsights: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  if (!id) {
    return (
      <Alert
        message="Error"
        description="Guide ID is required to load insights."
        type="error"
        showIcon
      />
    );
  }

  return <InsightsTable guideId={id} />;
};